import React, { useMemo, useState, useCallback, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions, ApexAxisChartSeries } from "apexcharts";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface MaxPlayerGame {
  rel_offset: number;
  game_date: string | number | null;
  g_match_id: string | null;
  opponent: string | null;
  stat_value: number | null;
  is_anchor: boolean;
}

export interface MaxPlayer {
  player_id: string | number | null;
  player_name: string;
  anchor_game_date: string | number | null;
  anchor_opponent: string | null;
  anchor_stat: number | null;
  games: MaxPlayerGame[];
}

export interface MaxGraphData {
  query_type: string;
  chart_type: string;
  sql_query?: string;
  labels: string[];
  values: number[];
  players: MaxPlayer[];
  total_records: number;
}

interface MaxGraphProps {
  data: MaxGraphData;
  chartReady?: boolean;
  statLabel?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (val: number | null | undefined): string =>
  val == null ? "N/A" : String(val);

const fmtDate = (val: string | number | null | undefined): string => {
  if (val == null || val === "" || val === "null") return "N/A";
  return String(val);
};

// ── Color System ──────────────────────────────────────────────────────────────
function hslToHex(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));
  return `#${[f(0), f(8), f(4)].map(v => v.toString(16).padStart(2, "0")).join("")}`;
}

const HUES = [4, 28, 48, 80, 142, 168, 192, 212, 240, 270, 300, 336];

const L_LEVELS: Array<{ s: number; l: number }> = [
  { s: 90, l: 50 },
  { s: 80, l: 38 },
  { s: 75, l: 28 },
  { s: 75, l: 62 },
  { s: 65, l: 72 },
];

const PALETTE: string[] = (() => {
  const out: string[] = [];
  for (const { s, l } of L_LEVELS) {
    for (const h of HUES) {
      out.push(hslToHex(h, s, l));
    }
  }
  return out;
})();

function getSequenceColor(index: number): string {
  return PALETTE[index % PALETTE.length];
}

function buildColorMap(players: MaxPlayer[]): Map<number, string> {
  const nameToColor = new Map<string, string>();
  let seqIdx = 0;
  const colorMap = new Map<number, string>();
  players.forEach((player, idx) => {
    const name = player.player_name;
    if (!nameToColor.has(name)) {
      nameToColor.set(name, getSequenceColor(seqIdx++));
    }
    colorMap.set(idx, nameToColor.get(name)!);
  });
  return colorMap;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=JetBrains+Mono:wght@400;500;600&display=swap');

  .mxg * { box-sizing: border-box; }

  .mxg {
    --bg: #ffffff;
    --surface: #f8fafc;
    --border: #e8edf3;
    --border-accent: #cbd5e1;
    --text-primary: #0f172a;
    --text-secondary: #475569;
    --text-muted: #94a3b8;
    --accent: #F59E0B;
    --accent-light: #fffbeb;
    --accent-border: #fde68a;
    --accent-text: #b45309;
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: var(--bg);
    border-radius: 24px;
    padding: 24px 28px;
    position: relative;
    width: 100%;
    border: 1px solid var(--border);
    box-shadow:
      0 1px 2px rgba(0,0,0,0.04),
      0 4px 16px rgba(0,0,0,0.06),
      0 16px 48px rgba(0,0,0,0.04);
  }

  .mxg::before {
    content: '';
    position: absolute;
    top: 0; right: 0;
    width: 260px; height: 160px;
    background: radial-gradient(ellipse at top right, rgba(245,158,11,0.07) 0%, transparent 65%);
    pointer-events: none;
    border-radius: 0 24px 0 0;
  }

  .mxg::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(148,163,184,0.18) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
    border-radius: 24px;
    opacity: 0.55;
    z-index: 0;
  }

  .mxg-chips-wrapper {
    position: relative;
    z-index: 2;
    margin-bottom: 18px;
  }

  .mxg-chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    padding: 12px 14px;
    background: var(--surface);
    border-radius: 14px;
    border: 1px solid var(--border);
    align-items: center;
  }

  .mxg-chip-all {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 13px;
    border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px;
    font-weight: 700;
    border: 1.5px solid var(--border-accent);
    background: white;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.18s ease;
    white-space: nowrap;
  }

  .mxg-chip-all:hover {
    background: var(--accent-light);
    border-color: var(--accent-border);
    color: var(--accent-text);
  }

  .mxg-chip-all.active {
    background: var(--accent-light);
    border-color: var(--accent-border);
    color: var(--accent-text);
  }

  /* ── Clear button — matches MinGraph: start of row, margin-right instead of margin-left ── */
  .mxg-chip-clear {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 13px;
    border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px;
    font-weight: 700;
    border: 1.5px solid #fca5a5;
    background: #fff5f5;
    color: #dc2626;
    cursor: pointer;
    transition: all 0.18s ease;
    white-space: nowrap;
    height: 34px;
    flex-shrink: 0;
    margin-right: 4px;
  }

  .mxg-chip-clear:hover {
    background: #fee2e2;
    border-color: #f87171;
    color: #b91c1c;
  }

  .mxg-chip {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 5px 10px 5px 8px;
    border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    border: 1.5px solid transparent;
    background: white;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
    height: 34px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }

  .mxg-chip:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    color: var(--text-primary);
  }

  .mxg-chip.active {
    border-color: var(--chip-border);
    background: var(--chip-bg);
    color: var(--chip-color);
    box-shadow: 0 2px 8px var(--chip-shadow);
  }

  .mxg-swatch {
    width: 7px; height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .mxg-chip.active .mxg-swatch {
    transform: scale(1.4);
    box-shadow: 0 0 6px var(--chip-color);
  }

  .mxg-chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 110px;
  }

  .mxg-chip-stat-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 6px;
    background: rgba(0,0,0,0.06);
    color: var(--text-secondary);
    flex-shrink: 0;
    transition: background 0.18s ease, color 0.18s ease;
  }

  .mxg-chip.active .mxg-chip-stat-badge {
    background: var(--chip-color);
    color: #ffffff;
  }

  .mxg-chip-check {
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .mxg-chart-wrapper {
    position: relative;
    z-index: 2;
    width: 100%;
    border-radius: 14px;
    overflow: visible;
    background: white;
    border: 1px solid var(--border);
  }

  .mxg-xaxis-labels {
    position: relative;
    z-index: 2;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 28px 2px 60px;
    margin-top: 0;
  }

  .mxg-zone-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #94a3b8;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .mxg-zone-label.prev  { color: #94a3b8; }
  .mxg-zone-label.anchor { color: #b45309; }
  .mxg-zone-label.next  { color: #94a3b8; }

  .mxg-zone-label .zone-arrow {
    opacity: 0.6;
    font-size: 10px;
  }

  .mxg-chart-wrapper .apexcharts-tooltip {
    z-index: 9999 !important;
    overflow: visible !important;
  }

  .mxg-empty {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--text-muted);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    gap: 8px;
  }

  .mxg-empty-icon { font-size: 28px; opacity: 0.35; }
`;

const MAX_VISIBLE_PLAYERS = 10;
const MAX_VISIBLE_INCREMENT = 10;
const MAX_DEFAULT_SELECTED = 3;
const MAX_MAX_SELECTED = 12;

// ── Component ──────────────────────────────────────────────────────────────────
const MaxGraph: React.FC<MaxGraphProps> = ({
  data,
  chartReady = true,
  statLabel = "Stat Value",
}) => {
  const { players, query_type } = data;

  const colorMap = useMemo(() => buildColorMap(players), [players]);

  const isSeason = useMemo(() => {
    const firstGame = players[0]?.games[0];
    if (!firstGame) return false;
    return /^\d{4}$/.test(String(firstGame.game_date ?? "").trim());
  }, [players]);

  const qt = String(query_type ?? "").trim().toLowerCase();
  const anchorLabel = qt === "min" || qt.startsWith("min_") ? "MIN" : "MAX";

  const [selectedPlayers, setSelectedPlayers] = useState<Set<number>>(
    () =>
      new Set(
        Array.from(
          { length: Math.min(MAX_DEFAULT_SELECTED, players.length) },
          (_, i) => i
        )
      )
  );
  const [visiblePlayersCount, setVisiblePlayersCount] = useState<number>(MAX_VISIBLE_PLAYERS);
  const [selectionLimitHit, setSelectionLimitHit] = useState(false);

  useEffect(() => {
    setVisiblePlayersCount(MAX_VISIBLE_PLAYERS);
    setSelectedPlayers(
      new Set(
        Array.from(
          { length: Math.min(MAX_DEFAULT_SELECTED, players.length) },
          (_, i) => i
        )
      )
    );
  }, [players.length]);

  useEffect(() => {
    if (!selectionLimitHit) return;
    const t = setTimeout(() => setSelectionLimitHit(false), 2500);
    return () => clearTimeout(t);
  }, [selectionLimitHit]);

  const togglePlayer = useCallback((idx: number) => {
    setSelectedPlayers(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
        return next;
      }
      if (next.size >= MAX_MAX_SELECTED) {
        setSelectionLimitHit(true);
        return prev;
      }
      next.add(idx);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setSelectedPlayers(new Set());
  }, []);

  const selectedIndicesOrdered = useMemo(
    () => Array.from(selectedPlayers).sort((a, b) => a - b),
    [selectedPlayers]
  );

  const xCategories = useMemo(() => {
    const offsets = new Set<number>();
    players.forEach(player => {
      player?.games.forEach(g => offsets.add(g.rel_offset));
    });
    return Array.from(offsets).sort((a, b) => a - b);
  }, [players]);

  const xCategoryLabels = useMemo(
    () =>
      xCategories.map(n => {
        if (n === 0) return anchorLabel;
        return n > 0 ? `+${n}` : `${n}`;
      }),
    [xCategories, anchorLabel]
  );

  const minOffset = xCategories.length > 0 ? xCategories[0] : -3;
  const maxOffset =
    xCategories.length > 0 ? xCategories[xCategories.length - 1] : 3;

  const series: ApexAxisChartSeries = useMemo(() => {
    return selectedIndicesOrdered.map(playerIdx => {
      const player = players[playerIdx];
      const sorted = [...player.games].sort((a, b) => a.rel_offset - b.rel_offset);
      const offsetMap = new Map(sorted.map(g => [g.rel_offset, g]));

      return {
        name: player.player_name,
        data: xCategories.map((offset, i) => ({
          x: xCategoryLabels[i],
          y: offsetMap.has(offset)
            ? (offsetMap.get(offset)!.stat_value ?? 0)
            : null,
        })),
      };
    });
  }, [players, selectedIndicesOrdered, xCategories, xCategoryLabels]);

  const options: ApexOptions = useMemo(() => ({
    chart: {
      type: "line",
      background: "transparent",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      animations: { enabled: true, speed: 650, easing: "easeinout" },
      height: 360,
    },
    colors: selectedIndicesOrdered.map(i => colorMap.get(i)),
    stroke: { curve: "smooth", width: 2.5, lineCap: "round" },
    markers: {
      size: 5,
      strokeWidth: 2.5,
      strokeColors: "#ffffff",
      strokeOpacity: 1,
      hover: { size: 8 },
      discrete: (() => {
        const anchors: any[] = [];
        selectedIndicesOrdered.forEach((playerIdx, seriesIdx) => {
          const color = colorMap.get(playerIdx);
          const sorted = [...players[playerIdx].games].sort(
            (a, b) => a.rel_offset - b.rel_offset
          );
          xCategories.forEach((offset, dataIdx) => {
            const game = sorted.find(g => g.rel_offset === offset);
            if (!game) return;
            if (game.is_anchor) {
              anchors.push({
                seriesIndex: seriesIdx,
                dataPointIndex: dataIdx,
                fillColor: color,
                strokeColor: "#ffffff",
                size: 9,
              });
            } else if (game.stat_value == null) {
              anchors.push({
                seriesIndex: seriesIdx,
                dataPointIndex: dataIdx,
                fillColor: "#ffffff",
                strokeColor: "#cbd5e1",
                size: 3,
              });
            }
          });
        });
        return anchors;
      })(),
    },
    xaxis: {
      type: "category",
      categories: xCategoryLabels,
      labels: {
        show: true,
        style: {
          colors: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        formatter: (val: string) => val,
      },
      axisBorder: { show: true, color: "#e8edf3", height: 1 },
      axisTicks: { show: false },
      title: {
        style: {
          color: "#94a3b8",
          fontSize: "9px",
          fontFamily: "'JetBrains Mono', monospace",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        formatter: (val: number) => val?.toFixed(0) ?? "N/A",
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      title: {
        text: statLabel,
        style: {
          color: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        offsetX: -4,
      },
    },
    annotations: {
      xaxis: [{
        x: anchorLabel,
        borderColor: "rgba(245,158,11,0.4)",
        borderWidth: 1.5,
        strokeDashArray: 5,
        label: {
          text: anchorLabel,
          position: "top",
          offsetY: -2,
          style: {
            color: "#b45309",
            background: "#fffbeb",
            fontSize: "11px",
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: "600",
            borderRadius: "4px",
            borderColor: "#fde68a",
            borderWidth: 1,
            padding: { left: 5, right: 5, top: 2, bottom: 2 },
          },
        },
      }],
    },
    grid: {
      show: true,
      borderColor: "#f1f5f9",
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 10, right: 20, bottom: 4, left: 8 },
    },
    legend: { show: false },
    tooltip: {
      theme: "light",
      shared: false,
      intersect: true,
      fixed: { enabled: false },
      custom: ({ seriesIndex, dataPointIndex }) => {
        if (seriesIndex >= selectedIndicesOrdered.length) return "";
        const playerIdx = selectedIndicesOrdered[seriesIndex];
        const player = players[playerIdx];
        const sorted = [...player.games].sort((a, b) => a.rel_offset - b.rel_offset);

        const offset = xCategories[dataPointIndex];
        const game = sorted.find(g => g.rel_offset === offset);
        if (!game) return "";

        const color = colorMap.get(playerIdx);
        const offsetLabel = xCategoryLabels[dataPointIndex];

        const statDisplay = fmt(game.stat_value);
        const dateDisplay = fmtDate(game.game_date);
        const opponentDisplay = game.opponent ?? "N/A";

        const labelLen = statLabel.length;
        const valueLen = statDisplay.length;
        const tooltipMinWidth = Math.min(
          300,
          Math.max(210, 130 + labelLen * 5 + valueLen * 10)
        );
        const dateLabel = isSeason ? "Season" : "Date";

        return `
          <div style="
            background:#ffffff;
            border:1px solid #e2e8f0;
            border-top:3px solid ${color};
            border-radius:14px;
            padding:13px 15px;
            font-family:'Plus Jakarta Sans',sans-serif;
            box-shadow:0 8px 30px rgba(0,0,0,0.12),0 2px 8px rgba(0,0,0,0.06);
            min-width:${tooltipMinWidth}px;
            width:max-content;
            max-width:320px;
            position:relative;
            z-index:9999;
          ">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
              <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
              <span style="font-weight:700;color:#0f172a;font-size:13px;letter-spacing:-0.01em;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
                ${player.player_name}
              </span>
              <span style="font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:600;color:${color};background:${color}15;padding:2px 8px;border-radius:6px;flex-shrink:0;">
                ${offsetLabel}
              </span>
            </div>

            <div style="display:grid;grid-template-columns:${isSeason ? "1fr" : "1fr 1fr"};gap:6px;margin-bottom:10px;">
              <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
                <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:3px;">${dateLabel}</div>
                <div style="font-size:11px;font-weight:600;color:#475569;">${dateDisplay}</div>
              </div>
              ${!isSeason ? `
              <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
                <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:3px;">vs</div>
                <div style="font-size:11px;font-weight:600;color:#475569;">${opponentDisplay}</div>
              </div>` : ""}
            </div>

            <div style="
              display:flex;
              align-items:center;
              justify-content:space-between;
              gap:${Math.max(12, Math.min(32, 8 + labelLen * 1.2))}px;
              background:${statDisplay === "N/A" ? "#f8fafc" : color + "10"};
              border:1px solid ${statDisplay === "N/A" ? "#e2e8f0" : color + "25"};
              border-radius:10px;
              padding:10px 14px;
            ">
              <span style="
                font-family:'JetBrains Mono',monospace;
                font-size:9px;
                font-weight:600;
                color:#64748b;
                text-transform:uppercase;
                letter-spacing:0.10em;
                line-height:1.45;
                white-space:normal;
                word-break:break-word;
                flex:1;
              ">${statLabel}</span>
              <span style="
                font-family:'Plus Jakarta Sans',sans-serif;
                font-size:${statDisplay === "N/A" ? 16 : Math.max(20, 22 - Math.max(0, valueLen - 3) * 3)}px;
                font-weight:800;
                color:${statDisplay === "N/A" ? "#94a3b8" : color};
                letter-spacing:-0.03em;
                line-height:1;
                flex-shrink:0;
                text-align:right;
              ">${statDisplay}</span>
            </div>
          </div>
        `;
      },
    },
    dataLabels: { enabled: false },
  }), [
    players,
    xCategories,
    xCategoryLabels,
    statLabel,
    selectedIndicesOrdered,
    anchorLabel,
    isSeason,
    colorMap,
  ]);

  if (!players || players.length === 0) {
    return (
      <>
        <style>{css}</style>
        <div className="mxg">
          <div className="mxg-empty">
            <span className="mxg-empty-icon">📭</span>
            <span>No data available</span>
          </div>
        </div>
      </>
    );
  }

  const visiblePlayers = players.slice(0, visiblePlayersCount);
  const remainingPlayers = Math.max(0, players.length - visiblePlayersCount);
  const nextIncrement = Math.min(MAX_VISIBLE_INCREMENT, remainingPlayers);

  return (
    <>
      <style>{css}</style>
      <div className="mxg">

        {/* ── Chips ── */}
        <div className="mxg-chips-wrapper">
          <div className="mxg-chips">

            {/* ── Clear button — at the START, only when something is selected (matches MinGraph) ── */}
            {selectedPlayers.size > 0 && (
              <button
                className="mxg-chip-clear"
                onClick={clearAll}
                title="Deselect all players"
              >
                ✕ Clear
              </button>
            )}

            {visiblePlayers.map((player, idx) => {
              const color = colorMap.get(idx);
              const active = selectedPlayers.has(idx);
              return (
                <button
                  key={idx}
                  className={`mxg-chip${active ? " active" : ""}`}
                  onClick={() => togglePlayer(idx)}
                  style={{
                    ["--chip-color" as any]: color,
                    ["--chip-bg" as any]: `${color}12`,
                    ["--chip-border" as any]: `${color}40`,
                    ["--chip-shadow" as any]: `${color}18`,
                  }}
                >
                  <span
                    className="mxg-swatch"
                    style={{ background: active ? color : "#cbd5e1" }}
                  />
                  <span className="mxg-chip-name">{player.player_name}</span>
                  {active && (
                    <span className="mxg-chip-check" style={{ color }}>✓</span>
                  )}
                </button>
              );
            })}

            {nextIncrement > 0 && (
              <button
                className="mxg-chip-all"
                onClick={() =>
                  setVisiblePlayersCount((c) =>
                    Math.min(players.length, c + MAX_VISIBLE_INCREMENT)
                  )
                }
              >
                +{nextIncrement} more
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "10px",
                    opacity: 0.75,
                  }}
                >
                  ({remainingPlayers})
                </span>
              </button>
            )}
          </div>

          {selectionLimitHit && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                fontWeight: 600,
                color: "#b45309",
              }}
            >
              You can select only {MAX_MAX_SELECTED} players at a time.
            </div>
          )}
        </div>

        {/* ── Chart ── */}
        <div className="mxg-chart-wrapper">
          {series.length > 0 && chartReady ? (
            <ReactApexChart
              type="line"
              series={series}
              options={options}
              height={360}
            />
          ) : (
            <div className="mxg-empty" style={{ height: 360 }}>
              <span className="mxg-empty-icon">↑</span>
              <span>Select players above to view trends</span>
            </div>
          )}
        </div>

        {/* ── Zone labels — hidden for season ── */}
        {series.length > 0 && chartReady && !isSeason && (
          <div className="mxg-xaxis-labels">
            <span className="mxg-zone-label prev">
              <span className="zone-arrow">←</span>
              PREV GAMES
              {minOffset < 0 && (
                <span style={{ opacity: 0.55, fontSize: "8px" }}>
                  ({Math.abs(minOffset)})
                </span>
              )}
            </span>
            <span className="mxg-zone-label next">
              NEXT GAMES
              {maxOffset > 0 && (
                <span style={{ opacity: 0.55, fontSize: "8px" }}>
                  ({maxOffset})
                </span>
              )}
              <span className="zone-arrow">→</span>
            </span>
          </div>
        )}

      </div>
    </>
  );
};

export default MaxGraph;